import { PayloadAction } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";
import { notificationsActions } from "modules/notifications/slices";
import { NOTIFICATION_TYPES } from "modules/notifications/types";
import { isPollStoppedSaga } from "modules/polling/sagas";
import { SagaIterator } from "redux-saga";
import { all, call, put, takeEvery, takeLeading } from "redux-saga/effects";
import { getAxiosErrorMessage } from "utils/getAxiosErrorMessage";
import { axiosInstance } from "../../axios";
import { securityActions } from "./slices";
import {
  AddPublicKeyParams,
  AddPublicKeyResponse,
  CreateKeyPairParams,
  CreateKeyPairResponse,
  DeleteKeyPairParams,
  DeleteKeyPairResponse,
  GetKeyPairsParams,
  GetKeyPairsResponse
} from "./types";

export function* getKeyPairsSaga(
  action: PayloadAction<GetKeyPairsParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId } = action.payload;
    const response: AxiosResponse<GetKeyPairsResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-compute/method/${projectId}/keypairs`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(securityActions.getKeyPairsSuccess(response.data));
    }
  } catch (e) {
    yield put(
      securityActions.getKeyPairsFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get key pairs data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* createKeyPairSaga(
  action: PayloadAction<CreateKeyPairParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Creating the key pair...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, data } = action.payload;
    const response: AxiosResponse<CreateKeyPairResponse> = yield call(
      axiosInstance.post,
      `gotham-${regionId}-compute/method/${projectId}/keypairs`,
      data
    );
    yield put(securityActions.createKeyPairSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Key pair has been successfully created.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      securityActions.createKeyPairFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to create key pair",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* addPublicKeySaga(
  action: PayloadAction<AddPublicKeyParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Adding the public key...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, data } = action.payload;
    const response: AxiosResponse<AddPublicKeyResponse> = yield call(
      axiosInstance.post,
      `gotham-${regionId}-compute/method/${projectId}/keypairs`,
      data
    );
    yield put(securityActions.addPublicKeySuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Public key has been successfully added.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      securityActions.addPublicKeyFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to add public key",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* deleteKeyPairSaga(
  action: PayloadAction<DeleteKeyPairParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Sending request to delete key pair...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, name } = action.payload;
    const response: AxiosResponse<DeleteKeyPairResponse> = yield call(
      axiosInstance.delete,
      `gotham-${regionId}-compute/method/${projectId}/keypairs/${name}`
    );
    yield put(securityActions.deleteKeyPairSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Request to delete key pair has been successfully sent.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      securityActions.deleteKeyPairFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to send request to delete key pair",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* watcherSaga(): SagaIterator<void> {
  yield all([
    takeLeading(securityActions.getKeyPairsRequest, getKeyPairsSaga),
    takeEvery(securityActions.createKeyPairRequest, createKeyPairSaga),
    takeEvery(securityActions.addPublicKeyRequest, addPublicKeySaga),
    takeEvery(securityActions.deleteKeyPairRequest, deleteKeyPairSaga)
  ]);
}
